import { ContextMenuAction } from '../models/TreeModel';
import { canNodeTypeHaveChildren } from './treeService';
import { addBreadcrumb } from '../../crash-dialog/services/breadcrumbService';
import { executeTableContextMenuAction } from '../../database-objects/services/tableContextMenuService';
import { executeViewContextMenuAction } from '../../database-objects/services/viewContextMenuService';
import { executeDatabaseContextMenuAction } from '../../database-objects/services/databaseContextMenuService';
import { executeColumnContextMenuAction } from '../../database-objects/services/columnContextMenuService';
import {
  isSecurityObjectType,
  executeSecurityContextMenuAction,
} from '../../database-objects/services/securityContextMenuService';
import {
  generateTablesFolderContextMenuActions,
  generateViewsFolderContextMenuActions,
  generateStoredProceduresFolderContextMenuActions,
  generateFunctionsFolderContextMenuActions,
  generatePackagesFolderContextMenuActions,
  executeFolderContextMenuAction,
} from '../../database-objects/services/folderContextMenuService';

/**
 * Context menu action types
 */
export enum ContextMenuActionType {
  REFRESH = 'refresh',
  // Script New actions for folders
  SCRIPT_NEW_DATABASE = 'script-new-database',
  SCRIPT_NEW_TABLE = 'script-new-table',
  SCRIPT_NEW_VIEW = 'script-new-view',
  SCRIPT_NEW_PROCEDURE = 'script-new-procedure',
  SCRIPT_NEW_FUNCTION = 'script-new-function',
  // Oracle-specific Script New actions
  SCRIPT_NEW_PACKAGE = 'script-new-package',
  SCRIPT_NEW_SEQUENCE = 'script-new-sequence',
  SCRIPT_NEW_SYNONYM = 'script-new-synonym',
  SCRIPT_NEW_TRIGGER = 'script-new-trigger',
  SCRIPT_NEW_DBLINK = 'script-new-dblink',
  // Security actions
  SCRIPT_NEW_LOGIN = 'script-new-login',
  SCRIPT_NEW_SERVER_ROLE = 'script-new-server-role',
  SCRIPT_NEW_USER = 'script-new-user',
  SCRIPT_NEW_DATABASE_ROLE = 'script-new-database-role',
  SCRIPT_NEW_SCHEMA = 'script-new-schema',
  SCRIPT_NEW_LOGIN_ROLE = 'script-new-login-role',
  SCRIPT_NEW_GROUP_ROLE = 'script-new-group-role',
  // Security object actions
  SCRIPT_AS_CREATE = 'script-as-create',
  SCRIPT_AS_ALTER = 'script-as-alter',
  SCRIPT_AS_DROP = 'script-as-drop',
  PROPERTIES = 'properties',
}

/**
 * Create a context menu refresh action
 */
export function createRefreshAction(): ContextMenuAction {
  return {
    id: ContextMenuActionType.REFRESH,
    label: 'Refresh',
    icon: 'refresh'
  };
}

/**
 * Create "Script New..." actions for folder types
 */
function createScriptNewActions(nodeType: string): ContextMenuAction[] {
  switch (nodeType) {
    case 'databases-folder':
      return [{
        id: ContextMenuActionType.SCRIPT_NEW_DATABASE,
        label: 'Script New Database...',
        icon: 'file-plus'
      }];

    case 'tables-folder':
      return [{
        id: ContextMenuActionType.SCRIPT_NEW_TABLE,
        label: 'Script New Table...',
        icon: 'file-plus'
      }];

    case 'views-folder':
      return [{
        id: ContextMenuActionType.SCRIPT_NEW_VIEW,
        label: 'Script New View...',
        icon: 'file-plus'
      }];

    case 'programmability-folder':
      return [
        {
          id: ContextMenuActionType.SCRIPT_NEW_PROCEDURE,
          label: 'Script New Stored Procedure...',
          icon: 'file-plus'
        },
        {
          id: ContextMenuActionType.SCRIPT_NEW_FUNCTION,
          label: 'Script New Function...',
          icon: 'file-plus'
        }
      ];

    case 'stored-procedures-folder':
      return [{
        id: ContextMenuActionType.SCRIPT_NEW_PROCEDURE,
        label: 'Script New Stored Procedure...',
        icon: 'file-plus'
      }];

    case 'functions-folder':
      return [{
        id: ContextMenuActionType.SCRIPT_NEW_FUNCTION,
        label: 'Script New Function...',
        icon: 'file-plus'
      }];

    // Oracle-specific folders
    case 'packages-folder':
      return [{
        id: ContextMenuActionType.SCRIPT_NEW_PACKAGE,
        label: 'Script New Package...',
        icon: 'file-plus'
      }];

    case 'sequences-folder':
      return [{
        id: ContextMenuActionType.SCRIPT_NEW_SEQUENCE,
        label: 'New Sequence...',
        icon: 'file-plus'
      }];

    case 'synonyms-folder':
      return [{
        id: ContextMenuActionType.SCRIPT_NEW_SYNONYM,
        label: 'New Synonym...',
        icon: 'file-plus'
      }];

    case 'db-links-folder':
      return [{
        id: ContextMenuActionType.SCRIPT_NEW_DBLINK,
        label: 'New DB Link...',
        icon: 'file-plus'
      }];

    // MSSQL Security folders
    case 'server-security-folder':
    case 'database-security-folder':
      return [{
        id: 'open-security-manager',
        label: 'Open Security Manager',
      }];

    case 'logins-folder':
      return [
        {
          id: 'open-security-manager',
          label: 'Open Security Manager',
        },
        {
          id: ContextMenuActionType.SCRIPT_NEW_LOGIN,
          label: 'New Login...',
          icon: 'file-plus'
        },
      ];

    case 'server-roles-folder':
      return [
        {
          id: 'open-security-manager',
          label: 'Open Security Manager',
        },
        {
          id: ContextMenuActionType.SCRIPT_NEW_SERVER_ROLE,
          label: 'New Server Role...',
          icon: 'file-plus'
        },
      ];

    case 'users-folder':
      return [{
        id: ContextMenuActionType.SCRIPT_NEW_USER,
        label: 'New User...',
        icon: 'file-plus'
      }];

    case 'database-roles-folder':
      return [{
        id: ContextMenuActionType.SCRIPT_NEW_DATABASE_ROLE,
        label: 'New Database Role...',
        icon: 'file-plus'
      }];

    case 'schemas-folder':
      return [{
        id: ContextMenuActionType.SCRIPT_NEW_SCHEMA,
        label: 'New Schema...',
        icon: 'file-plus'
      }];

    // PostgreSQL Security folders
    case 'pg-roles-folder':
      return [{
        id: 'open-security-manager',
        label: 'Open Security Manager',
      }];

    case 'login-roles-folder':
      return [
        {
          id: 'open-security-manager',
          label: 'Open Security Manager',
        },
        {
          id: ContextMenuActionType.SCRIPT_NEW_LOGIN_ROLE,
          label: 'New Login Role...',
          icon: 'file-plus'
        },
      ];

    case 'group-roles-folder':
      return [
        {
          id: 'open-security-manager',
          label: 'Open Security Manager',
        },
        {
          id: ContextMenuActionType.SCRIPT_NEW_GROUP_ROLE,
          label: 'New Group Role...',
          icon: 'file-plus'
        },
      ];

    default:
      return [];
  }
}

/**
 * Generate context menu actions for a node type
 */
export function generateActionsForNodeType(nodeType: string, engine?: string): ContextMenuAction[] {
  // Use specialized folder menu generators for object type folders
  switch (nodeType) {
    case 'tables-folder':
      return generateTablesFolderContextMenuActions();
    case 'views-folder':
      return generateViewsFolderContextMenuActions();
    case 'stored-procedures-folder':
      return generateStoredProceduresFolderContextMenuActions();
    case 'functions-folder':
      return generateFunctionsFolderContextMenuActions();
    case 'packages-folder':
      return generatePackagesFolderContextMenuActions();
    default:
      break;
  }

  const actions: ContextMenuAction[] = [];

  // Add "Script New..." actions for folder types
  const scriptNewActions = createScriptNewActions(nodeType);

  // MSSQL-only: allow restoring a .bak from the Databases folder (SSMS-style)
  if (nodeType === 'databases-folder' && (engine === 'mssql' || engine === 'mock')) {
    scriptNewActions.push({
      id: 'restore-bak',
      label: 'Restore Database (.bak)...',
      icon: 'upload',
    });
  }
  actions.push(...scriptNewActions);

  // Add separator if we have script actions and will add refresh
  if (scriptNewActions.length > 0 && canNodeTypeHaveChildren(nodeType)) {
    actions.push({ id: 'separator', label: '', isSeparator: true });
  }

  // Add refresh action to nodes that can have dynamically loaded children
  if (canNodeTypeHaveChildren(nodeType)) {
    actions.push(createRefreshAction());
  }

  return actions;
}

/** Folder types that have overview actions */
const FOLDER_TYPES_WITH_OVERVIEW = [
  'tables-folder',
  'views-folder',
  'stored-procedures-folder',
  'functions-folder',
  'packages-folder',
] as const;

/**
 * Execute a context menu action
 * This is a convenience method that dispatches actions to the appropriate handlers
 */
export function executeContextMenuAction(
  actionId: string,
  nodeId: string,
  handlers: {
    onRefresh?: (nodeId: string) => void;
    onOpenTerminal?: (connectionId: string, database: string) => void;
  },
  nodeType?: string
): void {
  // Track context menu action
  addBreadcrumb('click', `context-menu:${actionId}`);

  // First, handle node type-specific actions
  if (nodeType === 'table') {
    executeTableContextMenuAction(actionId, nodeId, handlers);
    return;
  } else if (nodeType === 'column') {
    // Column-level actions don't need extra handlers — `executeContextMenuAction`
    // doesn't have the node available, so the caller routes through `node`-aware
    // dispatch below. We need the column node to do anything useful, but the
    // current contract here is nodeType-only; column actions reach here only if
    // a wrapper supplies the node — we just no-op when that's missing.
    executeColumnContextMenuAction(actionId, nodeId);
    return;
  } else if (nodeType === 'view') {
    executeViewContextMenuAction(actionId, nodeId, handlers);
    return;
  } else if (nodeType === 'database') {
    executeDatabaseContextMenuAction(actionId, nodeId, handlers);
    return;
  } else if (nodeType && isSecurityObjectType(nodeType)) {
    executeSecurityContextMenuAction(actionId, nodeId, handlers);
    return;
  } else if (nodeType && FOLDER_TYPES_WITH_OVERVIEW.includes(nodeType as typeof FOLDER_TYPES_WITH_OVERVIEW[number])) {
    executeFolderContextMenuAction(actionId, nodeId, handlers);
    return;
  }

  // For generic actions or if the specific handler didn't handle it
  switch (actionId) {
    case ContextMenuActionType.REFRESH:
      if (handlers.onRefresh) {
        handlers.onRefresh(nodeId);
      }
      break;
    // Add more cases for different action types
    default:
      console.warn(`No handler defined for action type: ${actionId}`);
  }
} 
